import React from 'react';
import { Link } from 'react-router-dom';
import { Layout, Typography, Row, Col, Card, Space, Collapse, Checkbox } from 'antd';
import {
  DatabaseOutlined,
  ToolOutlined,
  TeamOutlined,
  CalendarOutlined,
  LineChartOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  CheckCircleOutlined,
  RightOutlined,
  UsergroupAddOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { SERVICE_TITLE } from '@shared/config/service-config';
import { PublicHeader } from '../components/public/PublicHeader';
import { SEOHead } from '../components/SEOHead';
import '../styles/public-buttons.css';
import '../styles/new-landing.css';

const { Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

// セクション1: ヒーローセクション
const HeroSection: React.FC = () => {
  return (
    <section className="hero-section">
      <div className="hero-background">
        <img
          src="/images/landing/hero-team.jpg"
          alt="工場で協力して作業するチーム"
          className="hero-bg-image"
        />
        <div className="hero-overlay"></div>
      </div>
      <div className="hero-content">
        <Row gutter={[48, 48]} align="middle" justify="center">
          <Col xs={24} lg={16} xl={14}>
            <div className="hero-text-center">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="hero-badge">設備管理の新しいカタチ</div>
                <Title level={1} className="hero-title">
                  設備管理の情報、<br />
                  バラバラになっていませんか?
                </Title>
                <Paragraph className="hero-subtitle">
                  設備・部品・消耗品・業者・作業履歴を1つに。<br />
                  チーム全員で使える設備管理クラウド
                </Paragraph>
                <Space size="middle" wrap style={{ justifyContent: 'center' }}>
                  <Link
                    className="public-btn public-btn--primary public-btn--lg"
                    to="/register"
                  >
                    無料登録で開始
                  </Link>
                  <Link
                    className="public-btn public-btn--ghost public-btn--lg"
                    to="/service/features"
                  >
                    サービス詳細を見る
                  </Link>
                </Space>
                <div className="hero-note">
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>
                    ✓ クレジットカード登録不要 / ✓ 無料登録
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
};

// セクション2: 課題セクション
const ProblemSection: React.FC = () => {
  const problems = [
    {
      icon: <DatabaseOutlined />,
      title: '情報がバラバラで探すのに時間がかかる',
      items: [
        '設備台帳はExcel、作業記録は紙、部品・消耗品在庫は別システム',
        '「この設備、いつメンテナンスした?」を調べるのに30分',
        '担当者が休むと、誰も分からない'
      ]
    },
    {
      icon: <TeamOutlined />,
      title: 'チームで情報共有できない',
      items: [
        'ベテランの頭の中にしかない情報が多い',
        'メンバーを増やしたいが、システムのユーザー上限がネック',
        'リアルタイムで進捗を把握できない'
      ]
    },
    {
      icon: <SafetyOutlined />,
      title: '予防保全が後手に回る',
      items: [
        '突発的な故障対応で疲弊',
        'いつ更新すべきか判断基準が曖昧',
        '重要設備の劣化状況が見えない'
      ]
    }
  ];

  return (
    <section className="problem-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>こんな課題、ありませんか?</Title>
          <Paragraph className="section-description">
            設備管理の現場でよくある3つの課題
          </Paragraph>
        </div>
        <Row gutter={[24, 24]}>
          {problems.map((problem, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className="problem-card">
                <div className="problem-card-header">
                  <div className="problem-icon">{problem.icon}</div>
                  <Title level={4}>{problem.title}</Title>
                </div>
                <ul className="problem-list">
                  {problem.items.map((item, idx) => (
                    <li key={idx}>
                      <Checkbox checked disabled />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// セクション3: 解決策セクション
const SolutionSection: React.FC = () => {
  const solutions = [
    {
      icon: <DatabaseOutlined />,
      title: 'すべてを1つに、一元管理',
      description: '設備台帳、部品・消耗品在庫、協力業者、作業履歴をまとめて管理',
      points: [
        '必要な情報に数秒でアクセス',
        '過去の作業内容や部品・消耗品交換履歴もすぐに確認',
        'バラバラだった情報を1箇所に集約'
      ]
    },
    {
      icon: <UsergroupAddOutlined />,
      title: 'チーム全員で使える',
      description: 'ユーザー数無制限(全プラン共通)',
      points: [
        '追加メンバーも追加料金なし',
        'リアルタイムで情報共有',
        '誰がどの作業を担当しているか一目瞭然'
      ]
    },
    {
      icon: <LineChartOutlined />,
      title: 'データで計画的メンテナンス',
      description: '業界に合わせた2つのモードで設備を管理',
      points: [
        '健全度モード（工場向け）: ワイブル分布で詳細予測',
        '耐用年数モード（病院・ホテル向け）: シンプル管理',
        '業界別の自動設定で、すぐに使い始められる'
      ]
    }
  ];

  return (
    <section className="solution-section">
      <div className="solution-background">
        <img
          src="/images/landing/dx-tablet.jpg"
          alt="タブレットで設備管理"
          className="solution-bg-image"
        />
        <div className="solution-overlay"></div>
      </div>
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>{SERVICE_TITLE}なら、すべて解決できます</Title>
          <Paragraph className="section-description">
            3つの解決策で設備管理を効率化
          </Paragraph>
        </div>
        <Row gutter={[32, 32]}>
          {solutions.map((solution, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className="solution-card">
                <div className="solution-icon-wrapper">
                  <div className="solution-icon">{solution.icon}</div>
                </div>
                <Title level={4}>{solution.title}</Title>
                <Paragraph className="solution-description">
                  {solution.description}
                </Paragraph>
                <ul className="solution-points">
                  {solution.points.map((point, idx) => (
                    <li key={idx}>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      {point}
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// セクション4: 主要機能セクション
const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: <DatabaseOutlined />,
      title: '設備台帳管理',
      description: '設備情報の登録・編集・検索',
      details: ['写真・図面の添付', '設備ごとの作業履歴表示', 'カスタム項目対応']
    },
    {
      icon: <ToolOutlined />,
      title: '部品・消耗品管理',
      badge: 'NEW',
      description: '部品・消耗品在庫の管理',
      details: ['発注履歴、交換履歴の記録', '設備との紐付け管理', '在庫アラート機能']
    },
    {
      icon: <TeamOutlined />,
      title: '業者管理',
      badge: 'NEW',
      description: '協力会社情報の一元管理',
      details: ['業者情報の登録', '作業実績の記録', '連絡先管理']
    },
    {
      icon: <CalendarOutlined />,
      title: '保全カレンダー',
      description: 'メンテナンス予定の可視化',
      details: ['担当者アサイン', '作業実績の記録', 'リマインド通知']
    },
    {
      icon: <LineChartOutlined />,
      title: '健全度予測',
      description: '業界に合わせた2つのモード',
      details: ['健全度モード: ワイブル分布で詳細予測', '耐用年数モード: シンプル管理', '業界別の自動設定']
    },
    {
      icon: <FolderOpenOutlined />,
      title: '書類台帳・法定点検管理',
      badge: 'NEW',
      description: '書類と提出履歴を一元管理',
      details: ['書類の登録・検索・関連付け', '改版履歴と変更メモの管理', '法定点検の提出・承認・差戻し']
    },
    {
      icon: <FileTextOutlined />,
      title: '作業指示書・履歴管理',
      description: '作業内容のテンプレート化',
      details: ['実績記録と写真添付', 'ナレッジの蓄積', '標準作業手順の共有']
    }
  ];

  return (
    <section className="features-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>主要機能</Title>
          <Paragraph className="section-description">
            設備管理に必要な機能をすべて搭載
          </Paragraph>
        </div>
        <Row gutter={[24, 24]}>
          {features.map((feature, index) => (
            <Col xs={24} sm={12} lg={8} key={index}>
              <Card className="feature-card" hoverable>
                <div className="feature-header">
                  <div className="feature-icon">{feature.icon}</div>
                  {feature.badge && (
                    <span className={`feature-badge ${feature.badge === 'NEW' ? 'badge-new' : 'badge-option'}`}>
                      {feature.badge}
                    </span>
                  )}
                </div>
                <Title level={4}>{feature.title}</Title>
                <Paragraph className="feature-description">{feature.description}</Paragraph>
                <ul className="feature-details">
                  {feature.details.map((detail, idx) => (
                    <li key={idx}>
                      <RightOutlined style={{ fontSize: 12, color: '#1677ff' }} />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </section>
  );
};

// セクション5: 料金プランセクション
const PricingSection: React.FC = () => {
  const plans = [
    {
      name: 'スターター',
      price: '19,800',
      equipment: '50台まで',
      users: '無制限',
      storage: '20GB',
      description: '小規模導入・試用に最適',
      popular: false
    },
    {
      name: 'スタンダード',
      price: '49,800',
      equipment: '300台まで',
      users: '無制限',
      storage: '100GB',
      description: '小規模工場に最適',
      popular: true
    },
    {
      name: 'プロフェッショナル',
      price: '149,800',
      equipment: '1,000台まで',
      users: '無制限',
      storage: '400GB',
      description: '中規模工場・複数拠点に対応',
      popular: false
    }
  ];

  return (
    <section className="pricing-section">
      <div className="section-container">
        <div className="section-header">
          <Title level={2}>シンプルな料金体系</Title>
          <Paragraph className="section-description">
            ユーザー数無制限、明確な価格設定
          </Paragraph>
        </div>
        <Row gutter={[24, 24]} justify="center">
          {plans.map((plan, index) => (
            <Col xs={24} md={8} key={index}>
              <Card className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">人気</div>}
                <div className="pricing-header">
                  <Title level={4}>{plan.name}</Title>
                  <div className="pricing-price">
                    <span className="price-yen">¥</span>
                    <span className="price-amount">{plan.price}</span>
                    <span className="price-period">/月</span>
                  </div>
                </div>
                <ul className="pricing-features">
                  <li><CheckCircleOutlined /> 設備登録: {plan.equipment}</li>
                  <li><CheckCircleOutlined /> ユーザー: {plan.users}</li>
                  <li><CheckCircleOutlined /> ストレージ: {plan.storage}</li>
                </ul>
                <Paragraph className="pricing-description">{plan.description}</Paragraph>
                <Link
                  className={`public-btn ${plan.popular ? 'public-btn--primary' : ''} public-btn--block`}
                  to="/register"
                >
                  無料登録を開始
                </Link>
              </Card>
            </Col>
          ))}
        </Row>
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Link to="/service/plans" className="pricing-link">
            詳しいプラン比較を見る <RightOutlined />
          </Link>
        </div>
      </div>
    </section>
  );
};

// セクション6: FAQセクション
const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: '初期設定は難しくないですか?',
      answer: 'CSVでの一括登録に対応しています。また、初期導入サポートもご用意していますので、安心して始められます。'
    },
    {
      question: '既存のExcel台帳から移行できますか?',
      answer: 'はい、CSVインポート機能で簡単に移行できます。既存のExcelデータをCSV形式で保存していただければ、すぐにインポート可能です。'
    },
    {
      question: 'ユーザー数は本当に無制限ですか?',
      answer: 'はい、全プランでユーザー数制限はありません。チーム全員が追加料金なしでご利用いただけます。'
    },
    {
      question: '設備数が増えた場合は?',
      answer: '追加オプションで50台単位(月額20,000円)で追加可能です。プランアップグレードも柔軟に対応できます。'
    },
    {
      question: 'セキュリティは大丈夫ですか?',
      answer: 'SSL通信、データ暗号化、アクセス制御など万全の対策を実施しています。データセンターは東京とバックアップでシンガポールを使用しています。'
    }
  ];

  return (
    <section className="faq-section">
      <div className="section-container-narrow">
        <div className="section-header">
          <Title level={2}>よくある質問</Title>
          <Paragraph className="section-description">
            導入前の疑問にお答えします
          </Paragraph>
        </div>
        <Collapse
          accordion
          bordered={false}
          className="faq-collapse"
          expandIconPosition="end"
          items={faqs.map((faq, index) => ({
            key: index,
            label: <Text strong>{faq.question}</Text>,
            children: <Paragraph style={{ margin: 0 }}>{faq.answer}</Paragraph>
          }))}
        />
      </div>
    </section>
  );
};

// セクション7: CTAセクション
const CTASection: React.FC = () => {
  return (
    <section className="cta-section">
      <div className="cta-background">
        <img
          src="/images/landing/team-group.jpg"
          alt="チーム全員で取り組む"
          className="cta-bg-image"
        />
        <div className="cta-overlay"></div>
      </div>
      <div className="section-container-narrow">
        <Card className="cta-card">
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <Title level={2} style={{ margin: 0, color: 'white' }}>
              まずは無料登録で体験してください
            </Title>
            <div className="cta-features">
              <Row gutter={[16, 16]} justify="center">
                <Col xs={24} sm={12}>
                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                  <div>クレジットカード登録不要</div>
                </Col>
                <Col xs={24} sm={12}>
                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                  <div>全機能(数量制限あり)を利用可能</div>
                </Col>
              </Row>
            </div>
            <Link
              className="public-btn public-btn--primary public-btn--lg"
              to="/register"
            >
              今すぐ無料登録で開始
            </Link>
            <Space size="middle" wrap>
              <Text type="secondary">または、詳しい説明をご希望の方は</Text>
              <Link to="/contact" className="cta-link">
                お問い合わせ
              </Link>
            </Space>
          </Space>
        </Card>
      </div>
    </section>
  );
};

// メインコンポーネント
export const LandingPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="一元帳｜設備管理を一元化できる設備・部品・業者管理ソフト"
        description="設備管理の情報がバラバラで非効率になっていませんか？一元帳なら設備・部品・業者・作業履歴をまとめて一括管理。現場と管理部門で共有できる設備管理ソフトです。"
      />
      <Layout style={{ minHeight: '100vh', background: '#fff' }}>
        <PublicHeader />

      <Content>
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <FeaturesSection />
        <PricingSection />
        <FAQSection />
        <CTASection />
      </Content>

      <Footer className="public-footer">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap size={[16, 8]} style={{ justifyContent: 'center' }}>
            <a href="https://dio-meta.com/company" target="_blank" rel="noopener noreferrer">会社概要</a>
            <Link to="/service/features">機能紹介</Link>
            <Link to="/service/plans">料金プラン</Link>
            <Link to="/industry-guides">業界別デモ</Link>
            <Link to="/contact">お問い合わせ</Link>
            <Link to="/legal/terms-privacy">利用規約</Link>
            <a href="https://dio-meta.com/privacypolicy/" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
            <Link to="/legal/specified-commercial-transactions">特定商取引法</Link>
          </Space>
          <Text type="secondary" style={{ fontSize: 13 }}>© 2025 {SERVICE_TITLE}. All rights reserved. powered by AVS1866</Text>
        </Space>
      </Footer>
    </Layout>
    </>
  );
};

export default LandingPage;
